import { useMutation, useQueryClient } from "@tanstack/react-query";

import { errorHandler } from "#app/monitoring/errorHandler";
import { showErrorToast } from "#app/notifications";
import { useCompany } from "#modules/company/view/useCompany";
import { updatePartType } from "#modules/parts/infra/updatePartType.api";
import {
  applyOptimisticUpdatePartType,
  applyUpdatePartTypeSuccess,
  rollbackUpdatePartType,
  UpdatePartTypeCacheSnapshot,
} from "#modules/parts/infra/updatePartTypeCache";
import { AnalyticsEvent, useAnalytics } from "#shared/useAnalytics";
import { PartTypeType, JobStatusSchema, QuoteRequestLine } from "shared";

type MutationData = {
  newPartType: PartTypeType;
  newPartSubType: string | null;
  newQuoteRequestLineId?: string;
};

export const useUpdatePartType = ({
  partId,
  quoteRequestLine,
}: {
  partId: string;
  quoteRequestLine?: QuoteRequestLine;
}) => {
  const queryClient = useQueryClient();
  const analytics = useAnalytics();

  const { company } = useCompany();

  const quoteRequestLineId = quoteRequestLine?.id;

  const { mutate, ...rest } = useMutation({
    mutationFn: ({ newPartType, newPartSubType, newQuoteRequestLineId }: MutationData) => {
      const quoteRequestLineIdToUpdate = newQuoteRequestLineId || quoteRequestLine?.id;

      const errorMessage = "Un identifiant de ligne de devis est requis";

      if (!quoteRequestLineIdToUpdate) {
        showErrorToast({
          title: errorMessage,
        });
        throw new Error(errorMessage);
      }

      return updatePartType({
        id: partId,
        payload: {
          partType: newPartType,
          partSubType: newPartSubType,
          quoteRequestLineId: quoteRequestLineIdToUpdate,
        },
      });
    },

    onMutate: async ({ newPartType }: MutationData) => {
      if (!quoteRequestLineId) {
        return { snapshot: undefined };
      }

      const snapshot = await applyOptimisticUpdatePartType(queryClient, {
        partId,
        quoteRequestLineId,
        newPartType,
      });

      if (quoteRequestLine.predictionStatus === JobStatusSchema.enum.PROCESSED) {
        analytics.capture(AnalyticsEvent.PredictionRerun, {
          quote_request_line_id: quoteRequestLine.id,
          trigger: "updatePartType",
          company_id: company?.id ?? null,
          company_name: company?.name ?? null,
        });
      }

      return { snapshot };
    },

    onSuccess: (result, variables: MutationData) => {
      const quoteRequestLineIdToUpdate = quoteRequestLine?.id || variables.newQuoteRequestLineId;

      if (!quoteRequestLineIdToUpdate) {
        return;
      }

      applyUpdatePartTypeSuccess(queryClient, {
        partId,
        quoteRequestLineId: quoteRequestLineIdToUpdate,
        part: result.part,
        shouldAnalyzePart: result.shouldAnalyzePart,
        shouldPredictAfterAnalyze: result.shouldPredictAfterAnalyze,
      });
    },

    onError: (
      error: Error,
      _: MutationData,
      context: { snapshot: UpdatePartTypeCacheSnapshot | undefined } | undefined,
    ) => {
      showErrorToast({ title: error.message });
      errorHandler(error);

      if (context?.snapshot) {
        rollbackUpdatePartType(queryClient, context.snapshot);
      }
    },
  });

  return { updatePartType: mutate, ...rest };
};
